import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "../reconciliation-dashboard.css";
import {
  backfillProjectFundLedger,
  fetchProjectReconciliation,
  repairProjectCapTable,
} from "../services/reconciliationService";
import { downloadProjectMissingLedgerCsv } from "../utils/reconciliationExport";
import { ReconciliationProjectDetail } from "./reconciliationShared";

export default function AdminReconciliationProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState("");
  const [capRepairBusy, setCapRepairBusy] = useState(false);
  const [capRepairMsg, setCapRepairMsg] = useState("");

  const loadDetail = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await fetchProjectReconciliation(projectId);
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setError(e.response?.data?.detail || "Failed to load project reconciliation.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const runBackfill = async (dryRun) => {
    if (!projectId) return;
    setBackfillBusy(true);
    setBackfillMsg("");
    setError("");
    try {
      const { data } = await backfillProjectFundLedger(projectId, { dryRun });
      setBackfillMsg(
        dryRun
          ? `Preview: would create ${data.entries_created} fund ledger row(s).`
          : `Created ${data.entries_created} fund ledger row(s).`
      );
      if (data.reconciliation_after) setDetail(data.reconciliation_after);
      else if (!dryRun) await loadDetail();
    } catch (e) {
      setError(e.response?.data?.detail || "Backfill failed.");
    } finally {
      setBackfillBusy(false);
    }
  };

  const runCapRepair = async (dryRun) => {
    if (!projectId) return;
    setCapRepairBusy(true);
    setCapRepairMsg("");
    setError("");
    try {
      const { data } = await repairProjectCapTable(projectId, { dryRun });
      if (dryRun) {
        const p = data.planned || {};
        setCapRepairMsg(
          data.eligible
            ? `Preview: treasury +${p.credit_treasury || "0.00"} sh, cap restore +${p.restore_cap || "0.00"} sh.`
            : data.detail || `Not eligible: ${data.reason || "unknown"}.`
        );
      } else {
        setCapRepairMsg("Cap table repaired. Holdings should now match expected issued shares.");
        if (data.reconciliation_after) setDetail(data.reconciliation_after);
        else await loadDetail();
      }
    } catch (e) {
      const d = e.response?.data;
      setError(d?.detail || d?.reason || "Cap-table repair failed.");
    } finally {
      setCapRepairBusy(false);
    }
  };

  const handleRemediationAction = (uiAction) => {
    if (uiAction === "backfill_preview") runBackfill(true);
    else if (uiAction === "backfill_apply") runBackfill(false);
    else if (uiAction === "cap_table_repair_preview") runCapRepair(true);
    else if (uiAction === "cap_table_repair_apply") runCapRepair(false);
    else if (uiAction === "job_fix_shares_sold") {
      navigate("/admin/reconciliation", { state: { openJob: true } });
    }
  };

  const exportMissing = () => {
    if (!detail || !downloadProjectMissingLedgerCsv(detail)) {
      setCapRepairMsg("No missing ledger rows to export.");
      return;
    }
    setBackfillMsg("Missing ledger rows downloaded.");
  };

  return (
    <div className="recon-dash page recon-dash--project">
      <nav className="recon-detail-page__nav" aria-label="Breadcrumb">
        <button type="button" className="recon-back-btn" onClick={() => navigate("/admin/reconciliation")}>
          ← All projects
        </button>
        <button
          type="button"
          className="history-btn history-btn--secondary recon-detail-page__refresh"
          onClick={loadDetail}
          disabled={loading}
        >
          Refresh
        </button>
      </nav>

      {error ? <p className="form-error">{error}</p> : null}

      <ReconciliationProjectDetail
        detail={detail}
        loading={loading}
        backfillBusy={backfillBusy}
        backfillMsg={backfillMsg}
        capRepairBusy={capRepairBusy}
        capRepairMsg={capRepairMsg}
        onBackfill={runBackfill}
        onCapRepair={runCapRepair}
        onExportMissing={exportMissing}
        onRemediationAction={handleRemediationAction}
        fullPage
      />
    </div>
  );
}
